/**
 * Demo Payment Screens — MOCKUP for marketing screenshots only.
 *
 * Routes:
 *   /demo/odeme           -> fake PayTR-like checkout form
 *   /demo/odeme-basarili  -> success confirmation with receipt
 *
 * These pages DO NOT call any API; they render hard-coded demo data
 * to produce polished, realistic screenshots.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, CreditCard, Download, Lock, Shield, ArrowLeft, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';

const DEMO = {
  business: 'Akasya Sitesi',
  title: 'Mayıs 2026 Aidatı',
  amount: 750.0,
  currency: '₺',
  payerName: 'Mehmet Çetin',
  payerUnit: 'D-23',
  txId: 'PAY-202605-A23-00847',
  cardBrand: 'VISA',
  cardLast4: '4242',
  paidAt: '29.04.2026 14:23',
};

const formatTRY = (n) =>
  n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' TL';

/* ─────────────────── DEMO CHECKOUT FORM ─────────────────── */
export const DemoCheckout = () => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [card, setCard] = useState('4242 4242 4242 4242');
  const [exp, setExp] = useState('12 / 28');
  const [cvc, setCvc] = useState('123');
  const [name, setName] = useState('MEHMET ÇETİN');

  const onSubmit = (e) => {
    e.preventDefault();
    setProcessing(true);
    setTimeout(() => navigate('/demo/odeme-basarili'), 1500);
  };

  return (
    <div className="min-h-screen bg-[#F6F9FC] py-10 px-4 flex items-center justify-center">
      <div className="w-full max-w-4xl grid md:grid-cols-[1fr_1.2fr] gap-0 bg-white rounded-2xl shadow-xl overflow-hidden border border-[#E6E9EE]">
        {/* LEFT — summary */}
        <div className="bg-[#1A1A2E] text-white p-8 flex flex-col">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8" data-testid="demo-back-link">
            <ArrowLeft className="w-4 h-4" />
            Paydaai'a geri dön
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <img src="/logo-paydaai.png" alt="Paydaai" className="h-10 w-auto drop-shadow-md bg-white rounded-md p-1" />
            <span className="text-xs uppercase tracking-widest text-white/60">Güvenli Ödeme</span>
          </div>

          <p className="text-sm text-white/60">{DEMO.business} tarafından tahsil edilecek</p>
          <h1 className="text-2xl font-semibold mt-1" data-testid="demo-checkout-title">{DEMO.title}</h1>

          <div className="mt-8 mb-auto">
            <p className="text-sm text-white/60">Tutar</p>
            <p className="text-5xl font-bold tracking-tight" data-testid="demo-checkout-amount">{formatTRY(DEMO.amount)}</p>
          </div>

          <div className="mt-10 space-y-4 text-sm">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <Shield className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium">256-bit SSL şifreleme</p>
                <p className="text-white/60 text-xs">Kart bilgileri Paydaai sunucularında saklanmaz</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <Lock className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium">PCI-DSS sertifikalı altyapı</p>
                <p className="text-white/60 text-xs">3D Secure ile ek güvenlik</p>
              </div>
            </div>
          </div>

          <p className="mt-10 text-[11px] text-white/40">
            Paydaai bir ödeme kuruluşu değildir. Ödeme işlemleri lisanslı ödeme sağlayıcısı
            üzerinden gerçekleştirilir.
          </p>
        </div>

        {/* RIGHT — card form */}
        <form onSubmit={onSubmit} className="p-8" data-testid="demo-checkout-form">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-[#1A1A2E]">Ödeme Bilgileri</h2>
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#595959]">
              <span className="inline-block px-2 py-1 bg-[#1A1F71] text-white rounded">VISA</span>
              <span className="inline-block px-2 py-1 bg-[#EB001B] text-white rounded">MC</span>
              <span className="inline-block px-2 py-1 bg-[#0079BE] text-white rounded">AMEX</span>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-[#595959]">Kart Üzerindeki İsim</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 mt-1 font-medium tracking-wide" data-testid="demo-card-name" />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#595959]">Kart Numarası</Label>
              <div className="relative mt-1">
                <Input value={card} onChange={(e) => setCard(e.target.value)} className="h-11 pl-10 font-mono tracking-wider" data-testid="demo-card-number" />
                <CreditCard className="w-5 h-5 text-[#999] absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-[#595959]">Son Kullanma</Label>
                <Input value={exp} onChange={(e) => setExp(e.target.value)} className="h-11 mt-1 font-mono tracking-wider" data-testid="demo-card-exp" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#595959]">CVC</Label>
                <Input value={cvc} onChange={(e) => setCvc(e.target.value)} className="h-11 mt-1 font-mono tracking-wider" data-testid="demo-card-cvc" />
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-lg bg-[#F9F8F6] border border-[#E5E5E5] text-sm">
            <div className="flex justify-between text-[#595959]">
              <span>Ödenen:</span>
              <span>{DEMO.business} · {DEMO.title}</span>
            </div>
            <div className="flex justify-between mt-1 text-[#1A1A2E] font-semibold">
              <span>Toplam</span>
              <span>{formatTRY(DEMO.amount)}</span>
            </div>
          </div>

          <Button
            type="submit"
            disabled={processing}
            className="w-full h-12 mt-5 bg-[#2563EB] hover:bg-[#1E4ED8] text-white font-semibold text-base"
            data-testid="demo-pay-button"
          >
            {processing ? (
              <><svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 60" strokeLinecap="round" /></svg> İşleniyor…</>
            ) : (
              <><Lock className="w-4 h-4 mr-2" /> {formatTRY(DEMO.amount)} Öde</>
            )}
          </Button>

          <p className="text-[11px] text-[#999] text-center mt-3 leading-relaxed">
            Ödemenizi tamamlayarak <span className="font-medium text-[#2563EB]">Paydaai Hizmet Sözleşmesi</span>'ni
            ve <span className="font-medium text-[#2563EB]">Mesafeli Satış Sözleşmesi</span>'ni kabul etmiş sayılırsınız.
          </p>
        </form>
      </div>
    </div>
  );
};


/* ─────────────────── DEMO SUCCESS PAGE ─────────────────── */
export const DemoPaymentSuccess = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0FDF4] via-[#F6F9FC] to-white py-10 px-4 flex items-center justify-center">
      <div className="w-full max-w-xl">
        <Card className="border border-[#E5E5E5] shadow-xl overflow-hidden" data-testid="demo-success-card">
          {/* Success banner */}
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 px-8 pt-10 pb-8 text-white text-center relative">
            <div className="w-20 h-20 bg-white/15 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <CheckCircle2 className="w-12 h-12" strokeWidth={2} />
            </div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="demo-success-title">Ödemeniz Alındı!</h1>
            <p className="text-emerald-50 mt-2 text-sm">
              Teşekkürler. Dekontunuz kayıt altına alındı ve e-posta adresinize gönderildi.
            </p>
            <p className="text-4xl font-extrabold mt-5 tracking-tight" data-testid="demo-success-amount">
              {formatTRY(DEMO.amount)}
            </p>
          </div>

          <CardContent className="p-8 space-y-5">
            {/* Receipt details */}
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[#595959]">İşletme</span>
                <span className="font-medium text-[#1A1A2E]" data-testid="demo-success-business">{DEMO.business}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#595959]">Ödeme Başlığı</span>
                <span className="font-medium text-[#1A1A2E]">{DEMO.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#595959]">Ödeyen</span>
                <span className="font-medium text-[#1A1A2E]">{DEMO.payerName} ({DEMO.payerUnit})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#595959]">Kart</span>
                <span className="font-medium text-[#1A1A2E]">
                  {DEMO.cardBrand} <span className="font-mono">•••• {DEMO.cardLast4}</span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#595959]">Tarih</span>
                <span className="font-medium text-[#1A1A2E]">{DEMO.paidAt}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-[#E5E5E5]">
                <span className="text-[#595959]">İşlem No</span>
                <span className="font-mono text-xs text-[#1A1A2E]" data-testid="demo-success-tx-id">{DEMO.txId}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button
                variant="outline"
                className="h-11 border-[#2563EB] text-[#2563EB] hover:bg-[#F0FDF4]"
                data-testid="demo-download-receipt"
              >
                <Download className="w-4 h-4 mr-2" />
                Dekontu İndir
              </Button>
              <Button
                asChild
                className="h-11 bg-[#2563EB] hover:bg-[#1E4ED8] text-white"
                data-testid="demo-back-home"
              >
                <Link to="/dashboard">
                  <Home className="w-4 h-4 mr-2" />
                  Ana Sayfaya Dön
                </Link>
              </Button>
            </div>

            <p className="text-[11px] text-center text-[#999] pt-2 leading-relaxed">
              Ödeme işleminiz lisanslı ödeme sağlayıcısı üzerinden gerçekleştirilmiştir.
              <br />Herhangi bir sorun için destek@paydaai.com adresinden bize ulaşın.
            </p>
          </CardContent>
        </Card>

        {/* Paydaai footer branding */}
        <div className="flex items-center justify-center gap-2 mt-6 text-xs text-[#999]">
          <img src="/logo-paydaai.png" alt="Paydaai" className="h-5 w-auto opacity-70" />
          <span>ile güvenle</span>
        </div>
      </div>
    </div>
  );
};
